// src/hooks/useList/useListParams.ts
import { useCallback, useState, useEffect } from "react";
import { FilterMap, ListParams, SortOption } from "./types";
import { parseSortString } from "./utils";

interface UseListParamsOptions {
  defaultFilters?: FilterMap;
  defaultSort?: string | SortOption;
  defaultPageSize?: number;
  populate?: string | object;
  fields?: string | object;
}

/**
 * Hook to manage list parameters (pagination, filters, sorting)
 */
export function useListParams(options: UseListParamsOptions = {}): {
  params: ListParams;
  setPageIndex: (index: number) => void;
  setPageSize: (size: number) => void;
  setFilter: (key: string, value: any) => void;
  setFilters: (filters: FilterMap) => void;
  setSort: (sort?: SortOption) => void;
  resetParams: () => void;
} {
  const {
    defaultFilters = {},
    defaultSort,
    defaultPageSize = 20,
    populate,
    fields,
  } = options;

  // Parse string sort to sort option
  const initialSort =
    typeof defaultSort === "string"
      ? parseSortString(defaultSort)
      : defaultSort;

  // Initialize state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [filters, setFilters] = useState(defaultFilters);
  const [sort, setSort] = useState<SortOption | undefined>(initialSort);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPageIndex(0);
  }, [filters, sort]);

  useEffect(() => {
    setFilters(defaultFilters);
  }, [JSON.stringify(defaultFilters)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update filters with key-value pair
  const setFilter = useCallback(
    (key: any, value: any) => {
      setFilters((prevFilters: FilterMap) => {
        // Allow false / 0 as valid filter values; only clear on null/undefined/""
        if (value !== null && value !== undefined && value !== "") {
          if (prevFilters[key] === value) {
            return prevFilters;
          }
          return { ...prevFilters, [key]: value };
        }
        if (!(key in prevFilters)) {
          return prevFilters;
        }
        const newFilters = { ...prevFilters };
        delete newFilters[key];
        return newFilters;
      });
      setPageIndex(0);
    },
    [setFilters, setPageIndex]
  );

  // Reset all params to defaults
  const resetParams = useCallback(() => {
    setPageIndex(0);
    setPageSize(defaultPageSize);
    setFilters(defaultFilters);
    setSort(initialSort);
  }, [defaultFilters, defaultPageSize, initialSort]);

  // Combine all params
  const params: ListParams = {
    pagination: {
      pageIndex,
      pageSize,
    },
    filters,
    sort,
    populate,
    fields,
  };

  return {
    params,
    setPageIndex,
    setPageSize,
    setFilter,
    setFilters,
    setSort,
    resetParams,
  };
}
